import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import dossierService from "./dossierService";

export const getDossiers = createAsyncThunk(
  "dossiers/getDossiers",
  async (params = {}, thunkAPI) => {
    try {
      return await dossierService.getDossiers(params);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const getDossierById = createAsyncThunk(
  "dossiers/getDossierById",
  async (id, thunkAPI) => {
    try {
      return await dossierService.getDossierById(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const createDossier = createAsyncThunk(
  "dossiers/createDossier",
  async (payload, thunkAPI) => {
    try {
      return await dossierService.createDossier(payload);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const updateDossier = createAsyncThunk(
  "dossiers/updateDossier",
  async ({ id, data }, thunkAPI) => {
    try {
      return await dossierService.updateDossier({ id, data });
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const updateDossierValues = createAsyncThunk(
  "dossiers/updateDossierValues",
  async ({ id, values }, thunkAPI) => {
    try {
      return await dossierService.updateDossierValues({ id, values });
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const archiveDossier = createAsyncThunk(
  "dossiers/archiveDossier",
  async (id, thunkAPI) => {
    try {
      return await dossierService.archiveDossier(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

export const restoreDossier = createAsyncThunk(
  "dossiers/restoreDossier",
  async (id, thunkAPI) => {
    try {
      return await dossierService.restoreDossier(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error.message,
      );
    }
  },
);

const initialState = {
  dossiers: [],
  selectedDossier: null,

  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  },

  filters: {
    search: "",
    status: "",
    current_step: "",
    school_year: "",
  },

  isLoading: false,
  isCreating: false,
  isUpdating: false,

  error: null,
};

const updateDossierInList = (list, updatedDossier) => {
  return list.map((item) =>
    item._id === updatedDossier._id
      ? {
          ...item,
          ...updatedDossier,
        }
      : item,
  );
};

const dossierSlice = createSlice({
  name: "dossiers",

  initialState,

  reducers: {
    clearDossierError: (state) => {
      state.error = null;
    },

    setSelectedDossier: (state, action) => {
      state.selectedDossier = action.payload;
    },

    setDossierFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };

      state.pagination.page = 1;
    },

    setDossierPage: (state, action) => {
      state.pagination.page = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getDossiers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })

      .addCase(getDossiers.fulfilled, (state, action) => {
        state.isLoading = false;

        state.dossiers = action.payload?.data?.items || [];

        state.pagination =
          action.payload?.data?.pagination || initialState.pagination;
      })

      .addCase(getDossiers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(getDossierById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })

      .addCase(getDossierById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedDossier = action.payload?.data || null;
      })

      .addCase(getDossierById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(createDossier.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })

      .addCase(createDossier.fulfilled, (state, action) => {
        state.isCreating = false;

        if (action.payload?.data) {
          state.dossiers.unshift(action.payload.data);
        }
      })

      .addCase(createDossier.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload;
      })

      .addCase(updateDossier.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })

      .addCase(updateDossier.fulfilled, (state, action) => {
        state.isUpdating = false;

        if (action.payload?.data) {
          state.dossiers = updateDossierInList(
            state.dossiers,
            action.payload.data,
          );

          if (state.selectedDossier?._id === action.payload.data._id) {
            state.selectedDossier = {
              ...state.selectedDossier,
              ...action.payload.data,
            };
          }
        }
      })

      .addCase(updateDossier.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })

      .addCase(updateDossierValues.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })

      .addCase(updateDossierValues.fulfilled, (state, action) => {
        state.isUpdating = false;

        if (action.payload?.data) {
          state.selectedDossier = action.payload.data;

          state.dossiers = updateDossierInList(
            state.dossiers,
            action.payload.data,
          );
        }
      })

      .addCase(updateDossierValues.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })

      .addCase(archiveDossier.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })

      .addCase(archiveDossier.fulfilled, (state, action) => {
        state.isUpdating = false;

        if (action.payload?.data) {
          state.dossiers = updateDossierInList(
            state.dossiers,
            action.payload.data,
          );
        }
      })

      .addCase(archiveDossier.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })

      .addCase(restoreDossier.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })

      .addCase(restoreDossier.fulfilled, (state, action) => {
        state.isUpdating = false;

        if (action.payload?.data) {
          state.dossiers = updateDossierInList(
            state.dossiers,
            action.payload.data,
          );
        }
      })

      .addCase(restoreDossier.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearDossierError,
  setSelectedDossier,
  setDossierFilters,
  setDossierPage,
} = dossierSlice.actions;

export default dossierSlice.reducer;
